import React, { useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

interface ExperienceLevelFormData {
  experience_level: string;
  years_of_experience: string;
}

const schema = yup.object().shape({
  experience_level: yup.string().required('Please select an experience level'),
  years_of_experience: yup.string().required('Please select years of experience'),
});

interface ExperienceLevelFormProps {
  onSubmit: (data: ExperienceLevelFormData) => void;
  isSubmitted?: boolean;
  historicalData?: ExperienceLevelFormData;
  fetchAssessmentSessions: () => void;
}

const experienceLevels = [
  { value: 'Entry-Level', label: 'Entry-Level' },
  { value: 'Associate', label: 'Associate' },
  { value: 'Mid-Level', label: 'Mid-Level' },
  { value: 'Senior/Executive Level', label: 'Senior/Executive Level' },
];

const yearsOptions = [
  { value: '0-2 years', label: '0-2 years' },
  { value: '1-3 years', label: '1–3 years' },
  { value: '3-5 years', label: '3–5 years' },
  { value: '6-8 years', label: '6–8 years' },
  { value: '9-10 years', label: '9–10 years' },
  { value: '10+ years', label: '10+ years' },
];

const ExperienceLevelForm: React.FC<ExperienceLevelFormProps> = ({
  onSubmit,
  isSubmitted = false,
  historicalData,
  fetchAssessmentSessions,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<ExperienceLevelFormData>({
    resolver: yupResolver(schema),
    defaultValues: historicalData || {
      experience_level: '',
      years_of_experience: '',
    },
  });

  const [showBtn, setShowBtn] = React.useState(true);

  useEffect(() => {
    if (historicalData) {
      setValue('experience_level', historicalData.experience_level);
      setValue('years_of_experience', historicalData.years_of_experience);
    }
  }, [historicalData, setValue]);

  const handleFormSubmit: SubmitHandler<ExperienceLevelFormData> = (data) => {
    onSubmit(data);
    setShowBtn(false);
    fetchAssessmentSessions();
  };

  const experienceLevel = watch('experience_level');
  const yearsOfExperience = watch('years_of_experience');

  return (
    <div className={`w-full pl-[50px] bg-transparent rounded-lg ${isSubmitted ? 'pointer-events-none' : ''}`}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        {/* Experience Level Section */}
        <div className="space-y-3">
          <h3 className="text-base font-semibold text-gray-900">
            What level of experience are you looking for?
          </h3>
          <p className="text-sm text-gray-600">
            Choose the appropriate level of experience for the role:
          </p>
          <div className="space-y-2">
            {experienceLevels.map((level) => (
              <label
                key={level.value}
                className="flex items-center space-x-3 cursor-pointer group"
                onClick={()=> setShowBtn(true)}
              >
                <input
                  type="radio"
                  value={level.value}
                  {...register("experience_level")}
                  disabled={isSubmitted}
                  className="
    w-4 h-4 border-gray-300 rounded-full cursor-pointer
    accent-[#012169]
    disabled:opacity-50 disabled:cursor-not-allowed
    transition-all duration-150
  "
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">
                  {level.label}
                </span>
              </label>
            ))}
          </div>
          {errors.experience_level && (
            <p className="text-sm text-red-600">{errors.experience_level.message}</p>
          )}
        </div>

        {/* Years of Experience Section */}
        <div className="space-y-3 pt-4 border-t border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">
            Roughly how many years of experience should they have for this present role?
          </h3>
          <p className="text-sm text-gray-600">
            Select the range of years of experience you are looking for:
          </p>
          <div className="space-y-2">
            {yearsOptions.map((option) => (
              <label
                key={option.value}
                className="flex items-center space-x-3 cursor-pointer group"
              >
                <input
                  type="radio"
                  value={option.value}
                  {...register('years_of_experience')}
                  className="
    w-4 h-4 border-gray-300 rounded-full cursor-pointer
    accent-[#012169]
    disabled:opacity-50 disabled:cursor-not-allowed
    transition-all duration-150
  "
                  disabled={isSubmitted}
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
          {errors.years_of_experience && (
            <p className="text-sm text-red-600">{errors.years_of_experience.message}</p>
          )}
        </div>

        {!isSubmitted && experienceLevel && yearsOfExperience && showBtn && (
          <div className="pt-4 flex justify-end">
            <button
              type="submit"
              className="
    mt-[20px] font-normal bg-[#D1E9FF] border border-[#1849A9] rounded-[10px]
    px-4 py-2 text-[#344054] cursor-pointer flex items-start w-fit shrink-0 h-full
    transition-all duration-200 ease-in-out
    hover:-translate-y-[2px] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]
    hover:bg-[#1849A9] hover:text-white
  "
            >
              Continue
            </button>

          </div>
        )}
      </form>
    </div>
  );
};

export { ExperienceLevelForm };
export type { ExperienceLevelFormData };
